import { useQuery } from '@tanstack/react-query'
import { ApiError, analyticsFetch, getCallerAgentId, hasLiveAnalytics } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import {
  fromFixtureRows,
  isUnavailable,
  toActivityReport,
  unavailableActivityReport,
  type AnalyticsActivityResponse,
  type ActivityReport,
} from '@/api/map/activity'
import * as api from '@/data/api'
import { keys } from '@/api/hooks/shared/keys'

async function loadActivity(
  limit: number,
  currency?: string,
  accountId?: string,
): Promise<ActivityReport> {
  if (hasLiveAnalytics()) {
    // Member Detail passes the selected downline id; Activity page leaves it
    // unset and reads the signed-in seat.
    const id = accountId ?? getCallerAgentId()
    if (!id) {
      return unavailableActivityReport({
        code: 'no_audit_source',
        reason: 'No agent id on the session — activity cannot be loaded.',
      })
    }
    const qs = new URLSearchParams({
      limit: String(Math.min(200, Math.max(1, limit))),
    })
    try {
      const live = await analyticsFetch<AnalyticsActivityResponse>(
        `/v1/agents/${id}/activity?${qs}`,
      )
      return toActivityReport(live, currency)
    } catch (err) {
      if (err instanceof ApiError && err.status === 503 && isUnavailable(err.body)) {
        return unavailableActivityReport(err.body)
      }
      if (isUnreachableApiError(err)) {
        return unavailableActivityReport({
          code: 'no_audit_source',
          reason: 'Activity could not be reached. No data found until the service responds.',
        })
      }
      throw err
    }
  }

  return fromFixtureRows(await api.listActivity(limit, accountId))
}

/** @param accountId When set, only that member's trail (Lab Detail scope). */
export function useActivity(limit = 60, currency?: string, accountId?: string) {
  return useQuery({
    queryKey: keys.activity(limit, currency, accountId),
    queryFn: () => loadActivity(limit, currency, accountId),
    placeholderData: (previous) => previous,
  })
}
